import { endOfMonth, format } from 'date-fns';
import { FC, useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import { buttonStyle } from 'components/buttons/buttonStyle';
import ActionHeader from 'components/forms/ActionHeader';
import FormWrapper from 'components/forms/form-components/FormWrapper';
import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput';
import DateInput from 'components/forms/inputs/rhf-inputs/DateInput';
import DynamicMultiSelectInput from 'components/forms/inputs/rhf-inputs/DynamicMultiSelectInput';
import RadioInput from 'components/forms/inputs/rhf-inputs/RadioInput';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import Section from 'components/section-menu/Section';
import SectionLayout from 'components/section-menu/SectionLayout';
import { Button } from 'components/ui/button';
import { useLazyGetMinGroupsListWithFiltersQuery, useLazyGetPartyGroupByIdQuery } from 'store/api/groups';
import { useLazyGetOpenInvoicesQuery } from 'store/api/openInvoices';
import { GroupMemberRoleType, PartyGroupMin } from 'types/groups';
import { errorAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { DEFAULT_DATE_FORMAT } from 'utils/parseDate';
import { generateUnpaidInvoicesExcel, UnpaidInvoicesGroupSection, UnpaidInvoicesView } from './unpaidInvoicesExcel';

interface UnpaidInvoicesFormValues {
    asOfDate: string;
    view: UnpaidInvoicesView;
    overdueMoreThanDays: string;
    separateGroups: boolean;
    groups: PartyGroupMin[];
}

const UnpaidInvoicesForm: FC = () => {
    // Computed on mount (not at module import) so the default reflects when the page is opened.
    const defaultValues = useMemo<UnpaidInvoicesFormValues>(
        () => ({
            asOfDate: format(endOfMonth(new Date()), DEFAULT_DATE_FORMAT),
            view: 'ACTIVE',
            overdueMoreThanDays: '',
            separateGroups: false,
            groups: [],
        }),
        []
    );
    const viewOptions = useMemo<{ label: string; value: UnpaidInvoicesView }[]>(
        () => [
            { label: t('unpaid-invoices.view.active'), value: 'ACTIVE' },
            { label: t('unpaid-invoices.view.collection-prevented'), value: 'COLLECTION_PREVENTED' },
        ],
        []
    );
    const methods = useForm<UnpaidInvoicesFormValues>({ defaultValues, mode: 'onChange' });
    const { control, handleSubmit, setValue } = methods;
    const view = useWatch({ control, name: 'view' });
    const separateGroups = useWatch({ control, name: 'separateGroups' });

    const [fetchOpenInvoices, { isFetching }] = useLazyGetOpenInvoicesQuery();
    const [fetchGroupById] = useLazyGetPartyGroupByIdQuery();
    const [isExporting, setIsExporting] = useState(false);

    const onSubmit = useCallback(
        async (formData: UnpaidInvoicesFormValues) => {
            setIsExporting(true);
            try {
                const rows = await fetchOpenInvoices({ asOfDate: formData.asOfDate }).unwrap();

                let groupSections: UnpaidInvoicesGroupSection[] = [];
                if (formData.view === 'ACTIVE' && formData.separateGroups && formData.groups.length > 0) {
                    const groupDetails = await Promise.all(
                        formData.groups.map((group) => fetchGroupById(String(group.id)).unwrap())
                    );
                    groupSections = groupDetails.map((group, index) => ({
                        groupName: formData.groups[index].groupName,
                        memberPartyIds: new Set(
                            (group.parties ?? [])
                                .filter(
                                    (partyGroupParty) =>
                                        partyGroupParty.groupMemberRoleTypeCode === GroupMemberRoleType.MEMBER
                                )
                                .map((partyGroupParty) => partyGroupParty.party.id)
                        ),
                    }));
                }

                await generateUnpaidInvoicesExcel(
                    rows,
                    formData.asOfDate,
                    formData.view,
                    formData.overdueMoreThanDays === '' ? null : Number(formData.overdueMoreThanDays),
                    groupSections
                );
            } catch {
                errorAlert(t('unpaid-invoices.generate-error'));
            } finally {
                setIsExporting(false);
            }
        },
        [fetchOpenInvoices, fetchGroupById]
    );

    const actions = useMemo(
        () => [
            <Button
                key="unpaid-invoices-export"
                {...buttonStyle({ variant: 'contained', color: 'secondary' })}
                disabled={isFetching || isExporting}
                onClick={() => {
                    void handleSubmit(onSubmit)();
                }}
            >
                <FormattedMessage id="unpaid-invoices.export" />
            </Button>,
        ],
        [handleSubmit, isFetching, isExporting, onSubmit]
    );

    return (
        <FormProvider {...methods}>
            <FormWrapper>
                <ActionHeader title={t('menu.unpaid-invoices')} actions={actions} />
                <SectionLayout>
                    <Section title="unpaid-invoices.section-title">
                        <div className="w-56">
                            <DateInput
                                control={control}
                                name="asOfDate"
                                label="unpaid-invoices.as-of-date"
                                required
                                fullWidth
                            />
                        </div>
                        <RadioInput
                            control={control}
                            setValue={setValue}
                            name="view"
                            label="unpaid-invoices.view-label"
                            options={viewOptions}
                            getOptionLabel="label"
                            getOptionIdentifier="value"
                            row
                        />
                        {view === 'ACTIVE' && (
                            <>
                                {/* A number input reports unparsable text such as "7.5" or "1e" as "", which would
                                    silently export without the filter; a digits-only text input never holds it. */}
                                <TextInput
                                    control={control}
                                    name="overdueMoreThanDays"
                                    label="unpaid-invoices.overdue-more-than-days"
                                    pattern="^[0-9]*$"
                                    inputProps={{ inputMode: 'numeric' }}
                                    wrapperClassName="w-56"
                                />
                                <CheckboxInput
                                    control={control}
                                    name="separateGroups"
                                    label="unpaid-invoices.separate-group-companies"
                                />
                                {separateGroups && (
                                    <DynamicMultiSelectInput
                                        setValue={setValue}
                                        control={control}
                                        label="unpaid-invoices.groups"
                                        placeholder="unpaid-invoices.groups"
                                        name="groups"
                                        paginated={{
                                            fetch: useLazyGetMinGroupsListWithFiltersQuery,
                                            searchField: 'groupName',
                                        }}
                                        getOptionLabel="groupName"
                                        getOptionIdentifier="id"
                                    />
                                )}
                            </>
                        )}
                    </Section>
                </SectionLayout>
            </FormWrapper>
        </FormProvider>
    );
};

export default UnpaidInvoicesForm;
